import React, { useState, useEffect } from 'react';
import { 
  CircularProgress, 
  Drawer, 
  IconButton, 
  Tooltip
} from '@mui/material';
import { 
  Close, 
  InfoOutlined, 
  Refresh, 
  WhatsApp, 
  Phone, 
  Email, 
  Person, 
  Business, 
  CalendarToday, 
  ArchiveOutlined,
  SourceOutlined,
  CategoryOutlined
} from '@mui/icons-material';
import moment from 'moment';

import { useAppData } from '../../../../../../context/AppDataContext';
import { useAlert } from '../../../../../../context/AlertContext';
import { useAuth } from '../../../../../../context/AuthContext';
import leadServices from '../leadServices';

import LeadForm from './LeadForm';
import LeadStatusBadge from './editor/LeadStatusBadge';
import LeadAssigneeBadge from './editor/LeadAssigneeBadge';
import LeadPriorityBadge from './editor/LeadPriorityBadge';
import LeadRemarkUpdate from './editor/LeadRemarkUpdate';
import LeadActivityList from './LeadActivityList';
import ConfirmDialog from '../../../../../../components/ConfirmDialog';

function LeadDetailsView({ leadDetails, setLeadDetails, callBack }) {
  const { userDD, getDDDataById, getMasterLabel } = useAppData();
  const { AppAlert } = useAlert();
  const { authUser } = useAuth();

  const [savingStatus, setSavingStatus] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [isAssignee, setIsAssignee] = useState(false);

  useEffect(() => {
    if (leadDetails && authUser) {
      setIsCreator(leadDetails.userId === authUser.id);
      setIsAssignee(leadDetails.assignedTo === authUser.id);
    }
    return () => {
      setIsCreator(false);
      setIsAssignee(false);
    };
  }, [leadDetails, authUser]);

  const updateLead = (payload) => {
    setSavingStatus(true);
    leadServices
      .update(payload)
      .then((res) => {
        AppAlert(res.msg);
        callBack();
      })
      .catch((error) => {
        AppAlert(error.msg || 'Update failed', 'error');
      })
      .finally(() => {
        setSavingStatus(false);
      });
  };

  const updateStatus = (statusPayload) => {
    const activity = {
      leadId: leadDetails.id,
      activityType: 'Status',
      oldValue: leadDetails.status,
      newValue: statusPayload.status,
      remark: statusPayload.remark || null,
    };
    const data = {
      id: leadDetails.id,
      status: statusPayload.status,
    };
    updateLead({ data, activity });
  };

  const updateAssignee = (assigneePayload) => {
    const activity = {
      leadId: leadDetails.id,
      activityType: 'Assignee',
      oldValue: leadDetails.assignedTo,
      newValue: assigneePayload.assignedTo,
      remark: assigneePayload.remark || null,
    };
    const data = {
      id: leadDetails.id,
      assignedTo: assigneePayload.assignedTo,
    };
    updateLead({ data, activity });
  };

  const updatePriority = (priorityPayload) => {
    const activity = {
      leadId: leadDetails.id,
      activityType: 'Priority',
      oldValue: leadDetails.priority,
      newValue: priorityPayload.priority,
      remark: priorityPayload.remark || null,
    };
    const data = {
      id: leadDetails.id,
      priority: priorityPayload.priority,
    };
    updateLead({ data, activity });
  };

  const updateRemark = (remarkPayload) => {
    const activity = {
      leadId: leadDetails.id,
      activityType: 'Remark',
      oldValue: null,
      newValue: null,
      remark: remarkPayload.remark,
    };
    const data = {
      id: leadDetails.id,
    };
    updateLead({ data, activity });
  };

  const archive = (id) => {
    setSavingStatus(true);
    leadServices
      .remove(id)
      .then(() => {
        AppAlert('Lead Archived');
        callBack();
      })
      .catch((error) => {
        AppAlert(error.msg || 'Archiving failed', 'error');
      })
      .finally(() => {
        setSavingStatus(false);
      });
  };

  const restore = (id) => {
    setSavingStatus(true);
    leadServices
      .restore(id)
      .then(() => {
        AppAlert('Lead Restored');
        callBack();
      })
      .catch((error) => {
        AppAlert(error.msg || 'Restoration failed', 'error');
      })
      .finally(() => {
        setSavingStatus(false);
      });
  };

  const creatorUser = leadDetails ? getDDDataById(userDD, leadDetails.userId) : null;

  return (
    <Drawer
      open={Boolean(leadDetails)}
      onClose={() => setLeadDetails(null)}
      anchor="right"
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '55%', lg: '45%' },
          maxWidth: '800px',
          bgcolor: '#f8f9fa',
        },
      }}
    >
      {leadDetails && (
        <div className="d-flex flex-column h-100 position-relative">
          {/* Header Bar with Estimated Value Integration */}
          <div className="bg-white px-4 py-3 border-bottom d-flex align-items-center justify-content-between sticky-top z-3">
            <div className="d-flex align-items-center gap-3">
              <span className="badge bg-primary-subtle text-primary fw-bold px-2 py-1">
                #{leadDetails.id}
              </span>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0 fw-bold text-dark">{leadDetails.businessName}</h5>
                </div>
                {creatorUser && (
                  <span className="text-muted small">
                    by {creatorUser.firstName} {creatorUser.lastName}
                  </span>
                )}
                {leadDetails.estimatedValue && (
                    <span className="badge bg-success-subtle text-success border border-success-subtle fw-bold ms-2 px-2 py-1" style={{ fontSize: '13px' }}>
                      ₹{Number(leadDetails.estimatedValue).toLocaleString()}
                    </span>
                  )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isCreator && (
                <LeadForm data={leadDetails} callBack={callBack} />
              )}

              {isCreator && leadDetails.deleted_at == null && (
                <ConfirmDialog id={leadDetails.id} confirm={archive}>
                  <Tooltip title="Archive Lead">
                    <button className="btn btn-outline-danger btn-sm rounded-circle p-2">
                      <ArchiveOutlined fontSize="small" />
                    </button>
                  </Tooltip>
                </ConfirmDialog>
              )}

              {isCreator && leadDetails.deleted_at !== null && (
                <Tooltip title="Restore Lead">
                  <button
                    className="btn btn-outline-success btn-sm rounded-circle p-2"
                    onClick={() => restore(leadDetails.id)}
                  >
                    <Refresh fontSize="small" />
                  </button>
                </Tooltip>
              )}

              <Tooltip title="Close Drawer">
                <IconButton
                  onClick={() => setLeadDetails(null)}
                  className="ms-1"
                  size="small"
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Body Content */}
          <div className="flex-grow-1 overflow-auto p-4">
            {savingStatus ? (
              <div className="d-flex justify-content-center align-items-center h-100 py-5">
                <CircularProgress />
              </div>
            ) : (
              <div className="d-flex flex-column gap-4">
                {/* Highlight Cards Grid (Reverted back to clean 4-card grid) */}
                <div className="row g-2">
                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 bg-white rounded-3 h-100">
                      <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                        <Business fontSize="small" /> Business Name
                      </div>
                      <span className="fw-semibold text-dark text-truncate">
                        {leadDetails.businessName || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 bg-white rounded-3 h-100">
                      <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                        <CategoryOutlined fontSize="small" /> Interested In
                      </div>
                      <span className="fw-semibold text-dark text-truncate">
                        {leadDetails.interestedIn || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 bg-white rounded-3 h-100">
                      <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                        <SourceOutlined fontSize="small" /> Source
                      </div>
                      <span className="fw-semibold text-dark text-truncate">
                        {getMasterLabel('lead-source', leadDetails.source) || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 bg-white rounded-3 h-100">
                      <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                        <CalendarToday fontSize="small" /> Due Date
                      </div>
                      <span className="fw-semibold text-dark text-truncate">
                        {leadDetails.dueDate
                          ? moment(leadDetails.dueDate).format('LL')
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Controls Section */}
                <div className="card border-0 shadow-sm p-3 bg-white rounded-3">
                  <h6 className="fw-bold mb-3 text-secondary small text-uppercase">
                    Quick Controls
                  </h6>
                  <div className="row g-3 align-items-center">
                    <div className="col-12 col-sm-6 col-md-3">
                      <label className="text-muted small mb-1 d-block">Status</label>
                      <LeadStatusBadge
                        value={leadDetails.status}
                        callBack={updateStatus}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <label className="text-muted small mb-1 d-block">Assignee</label>
                      <LeadAssigneeBadge
                        value={leadDetails.assignedTo}
                        callBack={updateAssignee}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <label className="text-muted small mb-1 d-block">Priority</label>
                      <LeadPriorityBadge
                        value={leadDetails.priority}
                        callBack={updatePriority}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-3">
                      <label className="text-muted small mb-1 d-block">Add Remark</label>
                      <LeadRemarkUpdate callBack={updateRemark} />
                    </div>
                  </div>
                </div>

                {/* Contact Cards Section */}
                <div className="row g-3">
                  {/* Primary Contact Card */}
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm h-100 bg-white rounded-3">
                      <div className="card-header bg-transparent border-0 pt-3 pb-0 px-3 fw-bold text-primary d-flex align-items-center gap-1">
                        <Person fontSize="small" /> Primary Contact
                      </div>
                      <div className="card-body p-3">
                        <div className="mb-2">
                          <span className="text-muted small d-block">Person Name</span>
                          <span className="fw-semibold text-dark">
                            {leadDetails.contactPerson1 || 'N/A'}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted small d-block">Phone Number</span>
                          {leadDetails.contactPhone1 ? (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <a
                                href={`tel:${leadDetails.contactPhone1}`}
                                className="btn btn-outline-primary btn-sm py-1 px-2 rounded-pill d-flex align-items-center gap-1"
                              >
                                <Phone fontSize="small" /> {leadDetails.contactPhone1}
                              </a>
                              <a
                                href={`https://wa.me/${leadDetails.contactPhone1}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-outline-success btn-sm p-1 rounded-circle d-flex align-items-center justify-content-center"
                              >
                                <WhatsApp fontSize="small" />
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                        <div>
                          <span className="text-muted small d-block">Email Address</span>
                          {leadDetails.contactEmail1 ? (
                            <a
                              href={`mailto:${leadDetails.contactEmail1}`}
                              className="text-primary text-decoration-none d-flex align-items-center gap-1 mt-1 small"
                            >
                              <Email fontSize="small" /> {leadDetails.contactEmail1}
                            </a>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Alternate Contact Card */}
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm h-100 bg-white rounded-3">
                      <div className="card-header bg-transparent border-0 pt-3 pb-0 px-3 fw-bold text-secondary d-flex align-items-center gap-1">
                        <Person fontSize="small" /> Secondary Contact
                      </div>
                      <div className="card-body p-3">
                        <div className="mb-2">
                          <span className="text-muted small d-block">Person Name</span>
                          <span className="fw-semibold text-dark">
                            {leadDetails.contactPerson2 || 'N/A'}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted small d-block">Phone Number</span>
                          {leadDetails.contactPhone2 ? (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <a
                                href={`tel:${leadDetails.contactPhone2}`}
                                className="btn btn-outline-primary btn-sm py-1 px-2 rounded-pill d-flex align-items-center gap-1"
                              >
                                <Phone fontSize="small" /> {leadDetails.contactPhone2}
                              </a>
                              <a
                                href={`https://wa.me/${leadDetails.contactPhone2}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-outline-success btn-sm p-1 rounded-circle d-flex align-items-center justify-content-center"
                              >
                                <WhatsApp fontSize="small" />
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                        <div>
                          <span className="text-muted small d-block">Email Address</span>
                          {leadDetails.contactEmail2 ? (
                            <a
                              href={`mailto:${leadDetails.contactEmail2}`}
                              className="text-primary text-decoration-none d-flex align-items-center gap-1 mt-1 small"
                            >
                              <Email fontSize="small" /> {leadDetails.contactEmail2}
                            </a>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Card */}
                <div className="card border-0 shadow-sm p-3 bg-white rounded-3">
                  <div className="d-flex align-items-center gap-1 text-secondary fw-bold small text-uppercase mb-2">
                    <InfoOutlined fontSize="small" /> Description
                  </div>
                  <div
                    className="p-3 bg-light rounded-2 text-dark small"
                    style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}
                  >
                    {leadDetails.details
                      ? leadDetails.details
                      : 'No additional details provided for this lead.'}
                  </div>
                </div>

                {/* Activity Feed Container */}
                <div className="card border-0 shadow-sm p-3 bg-white rounded-3">
                  <LeadActivityList leadId={leadDetails.id} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

export default LeadDetailsView;